// src/features/Units/LRCViewer.js
import React, { useState, useRef, useEffect } from 'react';
import { Square, Mic, RotateCcw, Play } from 'lucide-react';
import { AudioPlayer } from '../MediaLibrary';
import './LRCViewer.css';

const LRCViewer = ({ unitData, onComplete, completedUnits = {} }) => {
  const [questionText, setQuestionText] = useState('');
  const [questionAudio, setQuestionAudio] = useState('');
  const [modelAnswer, setModelAnswer] = useState('');
  const [image, setImage] = useState('');
  const [instructions, setInstructions] = useState('');

  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);

  const [hasRecorded, setHasRecorded] = useState(false);
  const [hasListenedToModel, setHasListenedToModel] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);

  const userAudioRef = useRef(null);
  const [isPlayingUser, setIsPlayingUser] = useState(false);

  const currentStatus = localStatus !== null ? localStatus : completedUnits[unitData?.id];
  const isCompleted = currentStatus === true || currentStatus === 'completed';
  const isStillLearning = currentStatus === 'still_learning';
  const shouldShowFeedback = (hasRecorded || !mediaRecorder) && hasListenedToModel && (!isCompleted || isStillLearning);

  // Parse content
  useEffect(() => {
    if (!unitData?.content) return;
    try {
      const parsed = typeof unitData.content === 'string'
        ? JSON.parse(unitData.content)
        : unitData.content;

      setQuestionText(parsed.questionText || '');
      setQuestionAudio(parsed.questionAudio || '');
      setModelAnswer(parsed.modelAnswer || '');
      setImage(parsed.image || '');
      setInstructions(parsed.instructions || '');
    } catch (error) {
      console.error('Error parsing LRC content:', error);
    }
  }, [unitData?.content]);

  // Initialize microphone
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          const recorder = new MediaRecorder(stream);
          setMediaRecorder(recorder);

          recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              const blob = new Blob([event.data], { type: 'audio/wav' });
              const url = URL.createObjectURL(blob);
              setRecordedUrl(url);
              setHasRecorded(true);
            }
          };
        })
        .catch(err => {
          console.warn('Microphone access denied or not available:', err);
        });
    }

    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, []);

  // Reset when unit changes
  useEffect(() => {
    if (unitData?.id) {
      setHasRecorded(false);
      setHasListenedToModel(false);
      setRecordedUrl(null);
      setLocalStatus(null);
    }
  }, [unitData?.id]);

  const playUserRecording = () => {
    if (userAudioRef.current && recordedUrl) {
      setIsPlayingUser(true);
      userAudioRef.current.play().catch(err => {
        console.error('Error playing user recording:', err);
        setIsPlayingUser(false);
      });
    }
  };

  const startRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'inactive') {
      setIsRecording(true);
      mediaRecorder.start();
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      setIsRecording(false);
      mediaRecorder.stop();
    }
  };

  const resetRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null);
    setHasRecorded(false);
  };

  const handleIKnowThis = () => {
    setLocalStatus(true);
    if (onComplete) onComplete(unitData.id, 'completed');
  };

  const handleStillLearning = () => {
    setLocalStatus('still_learning');
    if (onComplete) onComplete(unitData.id, 'still_learning');
  };

  const hasValidContent = questionText || questionAudio || modelAnswer;

  if (!hasValidContent) {
    return (
      <div className="lrc-container">
        <div className="lrc-no-content">
          <h2>Listen - Record - Compare</h2>
          <p>⚠️ No content loaded. Please check the unit configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lrc-container">
      {instructions && (
        <div className="lrc-instructions">
          <p>{instructions}</p>
        </div>
      )}

      {/* STEP 1: LISTEN & UNDERSTAND */}
      <div className="lrc-section listen">
        <h3>🎧 Step 1: Listen & Understand</h3>

        {image && (
          <img src={image} alt="Question visual" className="lrc-question-image" />
        )}

        {questionText && (
          <p className="lrc-question-text">{questionText}</p>
        )}

        {questionAudio && (
          <div className="lrc-audio-container">
            {/* Uses audioUrl prop — direct URL, no Firestore lookup */}
            <AudioPlayer audioUrl={questionAudio} />
          </div>
        )}
      </div>

      {/* STEP 2: PRACTICE (OPTIONAL) */}
      <div className="lrc-section practice">
        <h3>🎤 Step 2: Practice (Optional)</h3>

        <div className="lrc-recording-controls">
          {!isRecording ? (
            <button
              onClick={startRecording}
              disabled={!mediaRecorder}
              className="lrc-button record"
            >
              <Mic size={16} />
              Record Answer
            </button>
          ) : (
            <button onClick={stopRecording} className="lrc-button stop">
              <Square size={16} />
              Stop Recording
            </button>
          )}

          {hasRecorded && (
            <>
              <button onClick={resetRecording} className="lrc-button reset">
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                onClick={playUserRecording}
                disabled={isPlayingUser}
                className="lrc-button play-user"
              >
                <Play size={16} />
                {isPlayingUser ? 'Playing...' : 'Play Your Answer'}
              </button>
            </>
          )}
        </div>

        {!mediaRecorder && (
          <p className="lrc-recording-note">
            Recording not available on this device - practice speaking aloud!
          </p>
        )}

        <audio
          ref={userAudioRef}
          src={recordedUrl}
          onEnded={() => setIsPlayingUser(false)}
          style={{ display: 'none' }}
        />
      </div>

      {/* STEP 3: LEARN FROM MODEL */}
      {modelAnswer && (
        <div className="lrc-section learn">
          <h3>✨ Step 3: Learn from Model</h3>
          <div className="lrc-audio-container">
            {/* onPlayStart marks the model answer as listened to */}
            <AudioPlayer
              audioUrl={modelAnswer}
              onPlayStart={() => setHasListenedToModel(true)}
            />
          </div>
        </div>
      )}

      {/* FEEDBACK */}
      {shouldShowFeedback && (
        <div className="lrc-feedback">
          <div className="lrc-feedback-buttons">
            <button className="lrc-feedback-button know-this" onClick={handleIKnowThis}>
              ✅ I Know This
            </button>
            <button className="lrc-feedback-button still-learning" onClick={handleStillLearning}>
              🤔 Still Learning
            </button>
          </div>
        </div>
      )}

      {isStillLearning && (
        <div className="lrc-status-message still-learning">
          🤔 Still Learning - Review Again
        </div>
      )}

      {isCompleted && !isStillLearning && (
        <div className="lrc-status-message completed">
          ✅ Completed - You know this!
        </div>
      )}
    </div>
  );
};

export default LRCViewer;